import { useCallback, useMemo } from 'react';

import { useReportError } from '@/hooks/useReportError';
import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';
import { useMemoGenerationTrackerStateByMemoId } from '@/modules/memos/hooks/useMemoGenerationTrackerState';
import { useOnMemoGenerationComplete } from '@/modules/memos/hooks/useOnMemoGenerationComplete';
import { useMemosTableMemosQuery } from '@/modules/memos/MemosTable/graphql/MemosTable.generated';
import { useGeneratingDiagramArtifactIds } from '@/modules/workflowTrackers/hooks/useGeneratingDiagramArtifactIds';
import { useOnHouseholdWorkflowTrackerComplete } from '@/modules/workflowTrackers/hooks/useOnHouseholdWorkflowTrackerComplete';
import { WorkflowTrackerKind } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import { useDiagramArtifactsQuery } from '../../../graphql/DiagramArtifacts.generated';
import { toArtifactRows } from '../ArtifactsTable.utils';

/**
 * Loads the household diagrams and memos as artifact table rows, with the most
 * recently modified artifact first. Loads each list again when one of its
 * generations completes. Loads memos only when `memos_in_studio` is enabled.
 */
export function useArtifactRows(householdId: string) {
  const isMemosInStudioEnabled = useFeatureFlag('memos_in_studio');
  const diagramsQuery = useDiagramArtifactsQuery({
    variables: { householdID: householdId },
    fetchPolicy: 'cache-and-network',
  });
  const memosQuery = useMemosTableMemosQuery({
    variables: { householdId },
    fetchPolicy: 'cache-and-network',
    skip: !isMemosInStudioEnabled,
  });
  const loading = diagramsQuery.loading || memosQuery.loading;
  const error = diagramsQuery.error ?? memosQuery.error;
  useReportError('Failed to load saved artifacts', error);

  const { refetch: refetchDiagramsQuery } = diagramsQuery;
  const { refetch: refetchMemosQuery } = memosQuery;
  const refetchDiagrams = useCallback(() => {
    void refetchDiagramsQuery().catch(() => undefined);
  }, [refetchDiagramsQuery]);
  const refetchMemos = useCallback(() => {
    // Apollo sends the request on refetch, even when the query is skipped.
    if (!isMemosInStudioEnabled) return;
    void refetchMemosQuery().catch(() => undefined);
  }, [isMemosInStudioEnabled, refetchMemosQuery]);
  const refetch = useCallback(() => {
    refetchDiagrams();
    refetchMemos();
  }, [refetchDiagrams, refetchMemos]);

  // A completed generation changes the diagram and its last modified date.
  useOnHouseholdWorkflowTrackerComplete({
    kind: WorkflowTrackerKind.DiagramGeneration,
    onCompleted: refetchDiagrams,
  });
  useOnMemoGenerationComplete(householdId, refetchMemos);

  const generatingDiagramIds = useGeneratingDiagramArtifactIds();
  const memoTrackerStateById =
    useMemoGenerationTrackerStateByMemoId(householdId);
  const diagramsData = diagramsQuery.data?.diagramArtifacts;
  const memosData = memosQuery.data?.memos;
  const rows = useMemo(
    () =>
      toArtifactRows({
        diagrams: getNodes(diagramsData),
        memos: getNodes(memosData),
        generatingDiagramIds,
        generatingMemoIds: new Set(
          Object.keys(memoTrackerStateById).filter(
            (memoId) => memoTrackerStateById[memoId]?.isInProgress
          )
        ),
      }),
    [diagramsData, memosData, generatingDiagramIds, memoTrackerStateById]
  );

  return { rows, loading, error, refetch };
}
